'use client';

import { FaLink, FaTicketAlt } from 'react-icons/fa';
import {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toastSuccess, toastError } from '../utils/toasts';
import AdminSectionTitle from '../components/AdminSectionTitle';
import IconInfoCard from '../components/atoms/IconInfoCard';
import LoadingLottie from '../components/atoms/LoadingLottie';
import OrdersTableSkeleton from '../components/OrdersTableSkeleton';
import SelectTicketsTable, { TicketSelection } from '../components/SelectTicketsTable';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import {
  useGetAllTicketsQuery, useGetAudienceDiscountsQuery, useCreateOrderMutation, RootState, store,
  selectReservation, clearReservation, useGetCurrentAgentQuery, useGetQRImageQuery,
  useGetEventSubscriptionQuery, useGetBusinessEventAttendeeFieldsQuery, setTicketsSelected,
} from '../store';
import { eventsApi } from '../store/apis/events';
import TicketDTO from '../models/ticket';
import Accordion from '../components/atoms/Accordion';
import SectionTitle from '../components/atoms/SectionTitle';
import Input from '../components/atoms/Input';
import RadioButton from '../components/atoms/RadioButton';
import EventDTO, { EventAudienceDiscountDTO, EventPublicStatus } from '../models/event';
import { attendeeFieldType } from '../models/attendee-field';
import GotCoupon from '../components/GotCoupon';
import CardFrame from '../components/atoms/CardFrame';
import Button from '../components/atoms/Button';
import CouponDTO from '../types/Coupon';
import OrderSummary from '../components/OrderSummary';
import useGetUserRole from '../hooks/useGetUserRole';
import { OrderItemData, OrderDTO } from '../models/order';
import { OrderData, PayWayTokenData, NavePaymentData } from '../store/apis/orders';
import { PaymentType, PaymentProvider } from '../models/payment';
import { NaveCheckoutResult } from '../components/NaveCheckout';
import { CheckoutPaymentOptions, shouldShowSubmitButton } from '../components/checkout';
import useSendWhatsapp from '../hooks/useSendWhatsapp';
import generateWhatsappMessage from '../utils/generateWhatsappMessage';
import { orderSuccessPath } from '../utils/orderSuccessId';
import { getSelectionCodeCount } from '../utils/ticketQuantityPromo';
import Switch from '../components/atoms/Switch';
import copyLinkToClipboard from '../utils/copyLinkToClipboard';
import { registerOnlyDigits } from '../utils/onlyDigits';
import buildTicketShareMessage from '../utils/buildTicketShareMessage';
import CustomModal from '../components/atoms/CustomModal';
import useGetUserEventData from '../hooks/useGetUserEventData';
import useIsBackoffice from '../hooks/useIsBackoffice';
import Carrousel from '../components/atoms/Carrousel/Carrousel';
import { parseFriendlyDate } from '../utils/dates';
import PrintTicket from '../components/PrintTicket';
import { buildPrintTicketDataList, printTicketHtmlInNewWindow } from '../utils/printTickets';
import Select from '../components/atoms/Select/Select';
import NumeratedSeats from '../components/NumeratedSeats';
import { seatSelectionFor } from '../components/seat-map/buyerPlaces';
import { DisplaySeatDTO } from '../models/seat';
import useIsMobile from '../hooks/useIsMobile';
import { emailRegex, noSpaces } from '../utils/regexs';
import useEventPixel from '../hooks/useEventPixel';
import { trackViewContent, trackAddToCart, trackInitiateCheckout } from '../utils/metaPixelTracking';
import PrivateEventNoBalanceModal from '../components/PrivateEventNoBalanceModal';
import getErrorResponse from '../utils/getErrorResponse';
import parseErrors, { ErrorCode } from '../utils/parseErrors';
import { toPercentage } from '../utils/shared';
import {
  clearAbandonedCartToken,
  readAbandonedCartAttribution,
} from '../utils/abandonedCartTracking';
import useEventReferralCode from '../hooks/useEventReferralCode';

interface SimplifiedSalePageProps {
  eventpage?: boolean,
}

const SimplifiedSalePage = ({ eventpage = false }: SimplifiedSalePageProps) => {
  const navigate = useNavigate();
  const isBackoffice = useIsBackoffice();
  const role = useGetUserRole({ skip: !isBackoffice });
  const { event, eventLoading } = useGetSelectedEvent({ slug: !isBackoffice });
  const { code: refCode, isResolving: referralPending } = useEventReferralCode(event?.id);
  const { data: eventTickets, isLoading: ticketsLoading } = useGetAllTicketsQuery({ businessId: event?.business_id || '', eventId: event?.id || '', publicSaleAvailable: !isBackoffice }, { skip: !event });
  const { data: AudienceDiscounts } = useGetAudienceDiscountsQuery({
    businessId: event?.business_id || '',
    eventId: event?.id || '',
  }, { skip: !event?.business_id || !event?.id || !event?.has_audience_discounts });
  const reservation = useSelector((state: RootState) => state.orders.reservation);
  const ticketsSelected = useSelector((state: RootState) => state.tickets.ticketsSelected);
  const [allTickets, setAllTickets] = useState<TicketDTO[]>([]);
  const [entryTickets, setEntryTickets] = useState<TicketDTO[]>([]);
  const [extraTickets, setExtraTickets] = useState<TicketDTO[]>([]);
  const [ticketSelections, setTicketSelections] = useState<TicketSelection[]>([]);
  const [audienceDiscounts, setAudienceDiscounts] = useState<{ [key: number]: EventAudienceDiscountDTO | null }>({});
  const [appliedCoupon, setAppliedCoupon] = useState<CouponDTO | null>(null);
  const [hasCheckedReservation, setHasCheckedReservation] = useState(false);
  const dispatch = useDispatch<typeof store.dispatch>();
  const [createOrder, { isLoading: createOrderLoading }] = useCreateOrderMutation();
  const [shareMethod, setShareMethod] = useState<'share_link' | 'send_email'>(isBackoffice ? 'share_link' : 'send_email');
  const [customized, setCustomized] = useState<boolean>(event?.has_audience_discounts || false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [showNoBalanceModal, setShowNoBalanceModal] = useState<boolean>(false);
  const [shareData, setShareData] = useState<{ url: string; text: string; message: string } | null>(null);
  const [createdOrder, setCreatedOrder] = useState<OrderDTO | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const { data: qrImageData } = useGetQRImageQuery(
    { orderId: createdOrder?.id || '' },
    { skip: !createdOrder },
  );
  const userEventData = useGetUserEventData(event?.id || '', { skip: !isBackoffice });
  const [totalToPay, setTotalToPay] = useState<number>(0);
  const { data: agent } = useGetCurrentAgentQuery(
    { businessId: event?.business_id || '', eventId: event?.id || '' },
    { skip: !event || !event.allow_manual_payments || isBackoffice },
  );
  const [selectedShowtimeId, setSelectedShowtimeId] = useState<string | null>(null);
  const [selectedSeats, setSelectedSeats] = useState<DisplaySeatDTO[]>([]);
  const [ticketsTableResetKey, setTicketsTableResetKey] = useState<number>(0);
  const isMobile = useIsMobile();
  const [paymentMethod, setPaymentMethod] = useState<PaymentType | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider | null>(null);
  const [payWayTokenData, setPayWayTokenData] = useState<PayWayTokenData | null>(null);
  const [payWayFormError, setPayWayFormError] = useState<string | null>(null);
  const [isPayWayProcessing, setIsPayWayProcessing] = useState(false);
  const [, setNavePaymentData] = useState<NavePaymentData | null>(null);
  const [navePaymentRequestId, setNavePaymentRequestId] = useState<string | null>(null);
  const [isNaveProcessing, setIsNaveProcessing] = useState(false);
  const [clickedPay, setClickedPay] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const { data: eventSubscriptions } = useGetEventSubscriptionQuery(
    { businessId: event?.business_id || '', eventId: event?.id || '' },
    { skip: !event?.has_subscription || !event?.prints_subscriptions || !event?.business_id || !event?.id },
  );
  const [subscribedEvents, setSubscribedEvents] = useState<EventDTO[]>([]);
  const { data: attendeeFields } = useGetBusinessEventAttendeeFieldsQuery(
    { businessId: event?.business_id || '', eventId: event?.id || '' },
    { skip: !event?.business_id || !event?.id },
  );
  const activeAttendeeFields = useMemo(
    () => [...(attendeeFields ?? [])].filter((field) => field.active).sort((a, b) => a.order - b.order),
    [attendeeFields],
  );

  const isSellerGroupCorrect = (ticket: TicketDTO) => {
    if (!ticket.seller_groups || ticket.seller_groups.length === 0) {
      return true;
    }
    return ticket.seller_groups.some((sg) => sg.id === userEventData?.seller_group_id);
  };

  const ticketNameAndIdRequired = Boolean(
    !isBackoffice && event?.has_audience_discounts,
  );
  const isEmailRequired = !isBackoffice || (isBackoffice && shareMethod === 'send_email');

  const schema = useMemo(() => yup.object().shape({
    tickets: yup.array().of(yup.object().shape({
      full_name: ticketNameAndIdRequired
        ? yup.string().required('Debes ingresar un nombre')
        : yup.string(),
      person_id: ticketNameAndIdRequired
        ? yup.string().required('Debes ingresar un DNI')
        : yup.string(),
      // Custom attendee fields are keyed by field id; presence is enforced in processOrder.
      custom_fields: yup.mixed<Record<string, string>>(),
    })),
    email: isEmailRequired
      ? yup
        .string()
        .required('Debes ingresar un correo')
        .matches(emailRegex, 'Este correo no está bien')
        .test('no-spaces', 'No se permiten espacios', (value) => !value || noSpaces(value))
      : yup
        .string()
        .test('email-format', 'Este correo no está bien', (v) => !v || emailRegex.test(v))
        .test('no-spaces', 'No se permiten espacios', (value) => !value || noSpaces(value)),
    emailConfirm: isEmailRequired
      ? yup
        .string()
        .required('Debes ingresar un correo')
        .oneOf([yup.ref('email')], 'Los correos no coinciden')
        .test('no-spaces', 'No se permiten espacios', (value) => !value || noSpaces(value))
      : yup
        .string()
        .test('email-format', 'Este correo no está bien', (v) => !v || emailRegex.test(v))
        .oneOf([yup.ref('email')], 'Los correos no coinciden')
        .test('no-spaces', 'No se permiten espacios', (value) => !value || noSpaces(value)),
  }), [ticketNameAndIdRequired, isEmailRequired]);

  const {
    register, handleSubmit, formState, watch, getValues, reset, trigger,
  } = useForm({
    resolver: yupResolver(schema),
    shouldUnregister: true,
    defaultValues: {
      email: '',
      emailConfirm: '',
    },
  });

  useEffect(() => {
    if (isBackoffice) {
      setAllTickets(
        eventTickets?.filter((t) => (role === 'SELLER' && t.is_seller_ticket && t.is_seller_ticket && userEventData && isSellerGroupCorrect(t) && !t.is_only_admin)
          || ((role === 'ADMIN' || role === 'ROOT' || role === 'COPRODUCER'))) || [],
      );
    } else {
      setAllTickets(eventTickets || []);
    }
  }, [eventTickets]);

  useEffect(() => {
    if (event?.has_audience_discounts) {
      setCustomized(true);
    }
  }, [event?.has_audience_discounts]);

  useEffect(() => {
    if (selectedShowtimeId) {
      setEntryTickets(allTickets.filter((t) => !t.is_extra && !t.is_printed && t.showtime_id === selectedShowtimeId));
    } else {
      setEntryTickets(allTickets.filter((t) => !t.is_extra && !t.is_printed));
    }
    setExtraTickets(allTickets.filter((t) => t.is_extra && !t.is_printed));
  }, [allTickets, selectedShowtimeId]);

  useEffect(() => {
    if (event && event.has_numerated_seats && !hasCheckedReservation) {
      dispatch(selectReservation());
      setHasCheckedReservation(true);
    }
  }, [event, dispatch, hasCheckedReservation]);

  // Numbered events: one form row per item. A table of N is one place that
  // becomes N items sharing the seat id; general areas tapped on the map come
  // from redux (they were dropped here before).
  useEffect(() => {
    if (!event?.has_numerated_seats || !eventTickets || !reservation?.code || !event?.tickets) {
      return;
    }

    const expand = (selection: TicketSelection) => Array.from(
      { length: getSelectionCodeCount(selection.ticket, selection.quantity) },
      () => ({ ...selection, quantity: 1 }),
    );
    const seatSelections = selectedSeats.map((s): TicketSelection | null => {
      const ticket = eventTickets.find((t) => t.id === s.ticket_id) ?? event.tickets.find((t) => t.id === s.ticket_id);
      if (!ticket) {
        console.error(`Ticket not found for seat ID: ${s.id}, ticket ID: ${s.ticket_id}`);
        return null;
      }
      return seatSelectionFor(s, ticket);
    }).filter((t): t is TicketSelection => t !== null);
    const seatTicketIds = new Set(selectedSeats.map((s) => s.ticket_id));
    const generalPicks = ticketsSelected
      .filter((sel) => !sel.seat_id && !sel.ticket.event_sector?.is_numerated && !seatTicketIds.has(sel.ticket.id) && sel.quantity > 0);

    setTicketSelections([...seatSelections, ...generalPicks].flatMap(expand));
  }, [selectedSeats, eventTickets, reservation?.code, event?.tickets, ticketsSelected]);

  const handleTicketsChange = (selections: TicketSelection[], type: 'entry' | 'extra') => {
    const otherTypeSelections = ticketSelections.filter((item) => (type === 'entry' ? item.ticket.is_extra : !item.ticket.is_extra));
    const expandedSelections = selections.flatMap((selection) => {
      const individualTickets = [];
      // For buy_x_get_one, quantity = individual paid tickets; free ones are added
      // per completed bundle.
      const totalCount = getSelectionCodeCount(selection.ticket, selection.quantity);
      for (let i = 0; i < totalCount; i += 1) {
        individualTickets.push({
          ticket: selection.ticket,
          quantity: 1,
          seat_id: selection.seat_id,
          seat_name: selection.seat_name,
        });
      }
      return individualTickets;
    });

    setTicketSelections([...otherTypeSelections, ...expandedSelections]);
  };

  const handleCouponChange = (coupon: CouponDTO | null) => {
    setAppliedCoupon(coupon);
  };

  const resetTicketSelections = () => {
    setTicketSelections([]);
    setSelectedSeats([]);
    setAudienceDiscounts({});
    setAppliedCoupon(null);
    setTicketsTableResetKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (showShareModal) {
      resetTicketSelections();
      setTicketsTableResetKey((prev) => prev + 1);
      if (event?.has_numerated_seats) {
        dispatch(clearReservation());
        dispatch(setTicketsSelected([]));
        setSelectedSeats([]);
      }
    }
  }, [showShareModal, event?.has_numerated_seats, dispatch]);

  // Each visit is a new sale: general picks left in redux by a previous one must not come back.
  useEffect(() => {
    if (event?.has_numerated_seats) {
      dispatch(setTicketsSelected([]));
    }
  }, [event?.id]);

  useEffect(() => {
    setShowPaymentOptions(
      !!(
        event?.allow_manual_payments
        && agent?.phone_number
        && !isBackoffice
        && totalToPay && totalToPay > 0
      ),
    );
  }, [event, agent, isBackoffice, totalToPay]);

  // Initialize event-specific Meta Pixel for standalone route (not embedded in EventPage)
  useEventPixel(event, { enabled: !isBackoffice && !eventpage });

  useEffect(() => {
    if (event && !isBackoffice && !eventpage) {
      trackViewContent({
        content_name: event.name,
        content_type: 'product',
        content_ids: [event.id],
      });
    }
  }, [event?.id]);

  const subscriptionEventIdsKey = useMemo(
    () => (eventSubscriptions ?? []).map((s) => s.subscribed_event_id).join(),
    [eventSubscriptions],
  );

  useEffect(() => {
    let active = true;
    if (!event?.has_subscription || !event?.prints_subscriptions || !subscriptionEventIdsKey) {
      setSubscribedEvents([]);
      return () => {
        active = false;
      };
    }
    const ids = (eventSubscriptions ?? []).map((s) => s.subscribed_event_id);
    if (ids.length === 0) {
      setSubscribedEvents([]);
      return () => {
        active = false;
      };
    }
    Promise.all(
      ids.map((eventId) => dispatch(
        eventsApi.endpoints.getEventById.initiate({ eventId }),
      ).unwrap()),
    )
      .then((list) => {
        if (active) {
          setSubscribedEvents((list.filter(Boolean) as EventDTO[]));
        }
      })
      .catch(() => {
        if (active) {
          setSubscribedEvents([]);
        }
      });
    return () => {
      active = false;
    };
  }, [event?.has_subscription, event?.prints_subscriptions, subscriptionEventIdsKey, dispatch]);

  // Memoized order data for amounts calculation (excludes name/DNI to prevent unnecessary API calls)
  // Must be before early return to maintain consistent hook order
  const printTickets = useMemo(
    () => {
      if (!createdOrder || !event) {
        return [];
      }
      return buildPrintTicketDataList({
        order: createdOrder,
        event,
        qrImageData,
        subscribedEvents,
      });
    },
    [createdOrder, event, subscribedEvents, qrImageData],
  );

  const orderDataForAmounts = useMemo((): OrderData => ({
    email: getValues('email') || '',
    items: ticketSelections.map((item, index) => ({
      name: 'QR', // Placeholder - name/DNI don't affect pricing
      last_name: event?.name || 'QR',
      person_id: '0', // Placeholder - name/DNI don't affect pricing
      ticket_id: item.ticket.id,
      event_seat_id: item.seat_id,
      audience_discount_id: audienceDiscounts[index]?.id,
    })),
    coupon_id: appliedCoupon?.id,
    open_price_amount: 0,
    poll_responses: [],
    is_manual: isBackoffice,
    skip_poll: true,
    referral_code: '',
    from_backoffice: isBackoffice,
    is_seller_order: role === 'SELLER',
    reservation_code: event?.has_numerated_seats ? reservation?.code : undefined,
  }), [ticketSelections, audienceDiscounts, appliedCoupon, event, isBackoffice, role, reservation?.code]);

  if (eventLoading || !event) {
    return <LoadingLottie />;
  }

  const nameLabel = event.name_label || 'Nombre';
  const lastNameLabel = event.last_name_label || 'Apellido';
  const personIdLabel = event.person_id_label || 'DNI';
  const fullNameLabel = `${nameLabel} y ${lastNameLabel.toLocaleLowerCase('es-AR')}`;

  // Memoized orderItemData for display purposes (includes name/DNI from form)
  const orderItemData: OrderItemData[] = ticketSelections.map((item, index) => {
    const formIndex = customized ? index : 0;
    const formValues = watch(`tickets.${formIndex}`);
    return {
      name: formValues?.full_name || '',
      last_name: '',
      person_id: formValues?.person_id || '',
      ticket_id: item.ticket.id,
      ticket: item.ticket,
      ticket_extra: item.ticket.is_extra,
      seat_id: item.seat_id,
      seat_name: item.seat_name,
      audience_discount_id: audienceDiscounts[index]?.id,
      audience_discount: audienceDiscounts[index] || undefined,
      custom_fields: activeAttendeeFields
        .map((field) => ({
          field_id: field.id,
          label: field.label,
          value: (formValues?.custom_fields?.[field.id] ?? '').trim(),
        }))
        .filter((cf) => cf.value !== ''),
    };
  });

  const getPaymentProvider = (): PaymentProvider | undefined => {
    const isManual = paymentMethod === PaymentType.MANUAL || totalToPay === 0 || isBackoffice;
    if (isManual) {
      return PaymentProvider.AVANTTI;
    }
    if (paymentProvider === PaymentProvider.NAVE_LINK) {
      return PaymentProvider.NAVE;
    }
    return paymentProvider ?? undefined;
  };

  const getOrderData = (paywayToken?: PayWayTokenData | null, navePayment?: NavePaymentData | null): OrderData => {
    // El link del mail de carrito abandonado aterriza en esta misma página
    // cuando el evento es de venta simplificada, así que la compra también se
    // atribuye acá.
    const abandonedCart = event && !isBackoffice
      ? readAbandonedCartAttribution(event.id, event.date)
      : null;

    return {
      email: getValues('email') || '',
      items: orderItemData.map((item) => ({
        name: item.name || 'QR',
        last_name: item.last_name || 'Manual',
        person_id: item.person_id || '0',
        ticket_id: item.ticket.id,
        event_seat_id: item.seat_id,
        audience_discount_id: item.audience_discount_id,
        custom_fields: item.custom_fields,
      })),
      coupon_id: appliedCoupon?.id,
      open_price_amount: 0,
      poll_responses: [],
      is_manual: paymentMethod === PaymentType.MANUAL || isBackoffice,
      skip_poll: true,
      referral_code: !isBackoffice && !referralPending ? (refCode || undefined) : undefined,
      abandoned_cart_token: abandonedCart?.token,
      abandoned_cart_step: abandonedCart?.step,
      abandoned_cart_attribution: abandonedCart?.attribution,
      from_backoffice: isBackoffice,
      is_seller_order: role === 'SELLER',
      reservation_code: event.has_numerated_seats ? reservation?.code : undefined,
      payment_provider: getPaymentProvider(),
      payway_token: getPaymentProvider() === PaymentProvider.PAYWAY ? paywayToken || undefined : undefined,
      nave_payment: getPaymentProvider() === PaymentProvider.NAVE ? navePayment || undefined : undefined,
    };
  };

  const processOrder = (tokenData?: PayWayTokenData | null, navePayment?: NavePaymentData | null) => {
    if (referralPending) {
      return;
    }
    if (ticketSelections.length === 0 && selectedSeats.length === 0) {
      toastError('No hay entradas seleccionadas');
      return;
    }

    const mandatoryFields = activeAttendeeFields.filter((field) => field.mandatory);
    const missingMandatory = orderItemData.some(
      (item) => mandatoryFields.some(
        (field) => !item.custom_fields?.some((cf) => cf.field_id === field.id),
      ),
    );
    if (missingMandatory) {
      toastError('Completá todos los campos obligatorios de cada asistente');
      return;
    }

    // A `number` input can still hold junk ('12e', '1-2') on some browsers, so
    // the value is re-checked before it travels to the order as a string.
    const numberFields = activeAttendeeFields.filter(
      (field) => attendeeFieldType(field) === 'number',
    );
    const invalidNumber = orderItemData.some(
      (item) => numberFields.some((field) => {
        const value = item.custom_fields?.find((cf) => cf.field_id === field.id)?.value?.trim();
        return !!value && !Number.isFinite(Number(value));
      }),
    );
    if (invalidNumber) {
      toastError('Revisá los campos numéricos: sólo se aceptan números');
      return;
    }

    // Validate payment method is selected for non-backoffice users with payment required
    if (!isBackoffice && totalToPay && totalToPay > 0 && !paymentMethod) {
      toastError('Por favor seleccioná un método de pago');
      return;
    }

    // Validate payment provider is selected for online payments
    if (paymentMethod === PaymentType.ONLINE && totalToPay && totalToPay > 0 && !isBackoffice && !paymentProvider) {
      toastError('Por favor seleccioná un medio de pago');
      return;
    }

    if (!isBackoffice && event) {
      const uniqueIds = Array.from(new Set(ticketSelections.map((ts) => ts.ticket.id)));
      const totalItems = ticketSelections.reduce((sum, ts) => sum + ts.quantity, 0);
      const totalValue = ticketSelections.reduce((sum, ts) => sum + ts.ticket.price * ts.quantity, 0);
      trackAddToCart({
        content_name: event.name,
        content_type: 'product',
        content_ids: uniqueIds,
        value: totalValue,
        num_items: totalItems,
      });
      trackInitiateCheckout({
        content_name: event.name,
        content_ids: uniqueIds,
        value: totalValue,
        num_items: totalItems,
      });
    }

    setClickedPay(true);
    const action = createOrder({
      order: getOrderData(tokenData, navePayment),
      businessId: event.business_id,
      eventId: event.id,
    });

    const cleanupAfterOrderSuccess = () => {
      if (event.has_numerated_seats) {
        dispatch(clearReservation());
        setSelectedSeats([]);
      }
      resetTicketSelections();
      reset({
        ...getValues(),
        email: '',
        emailConfirm: '',
      });
      if (isBackoffice) {
        setShareMethod('share_link');
      }
      setClickedPay(false);
      setPayWayTokenData(null);
      setNavePaymentData(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    action
      .unwrap()
      .then((res) => {
        const od = res.data;
        if (od) {
          // La orden ya se creó con la atribución, así que el token cumplió su
          // función: se limpia para no contar dos veces una segunda compra.
          if (!isBackoffice) {
            clearAbandonedCartToken(event.id);
          }
          if (isBackoffice) {
            toastSuccess('Orden creada');
            setCreatedOrder(od);
            cleanupAfterOrderSuccess();
            if (shareMethod === 'share_link') {
              const orderUrl = `${window.location.origin}/${event?.slug}/${od.id}`;
              const { text, message } = buildTicketShareMessage(event, orderUrl, selectedShowtimeId);
              setShareData({ url: orderUrl, text, message });
              setShowShareModal(true);
            }
            if (shareMethod === 'send_email') {
              toastSuccess('Se ha enviado el correo electrónico con las entradas');
              resetTicketSelections();
            }
          } else {
            cleanupAfterOrderSuccess();
            if (totalToPay === 0 || od.status === 'paid') {
              navigate(orderSuccessPath(od.custom_id), { replace: true });
              return;
            }
            // For PayWay, if there's a payment token, the payment was already processed
            if (paymentProvider === PaymentProvider.PAYWAY && tokenData) {
              toastSuccess('¡Pago procesado exitosamente!');
              navigate(orderSuccessPath(od.custom_id), { replace: true });
              return;
            }
            // For Nave, if there's payment data with approved status, the payment was already processed
            if (paymentProvider === PaymentProvider.NAVE && navePayment?.status === 'approved') {
              toastSuccess('¡Pago procesado exitosamente!');
              navigate(orderSuccessPath(od.custom_id), { replace: true });
              return;
            }
            // For Nave, if we need to show the checkout, get payment request ID from response
            if (paymentProvider === PaymentProvider.NAVE && !navePayment) {
              const naveRequestId = od.payments?.[0]?.remote_id;
              if (naveRequestId) {
                setNavePaymentRequestId(naveRequestId);
                setClickedPay(false);
                return;
              }
            }
            const checkoutLink = od.payments?.[0]?.checkout_link;
            if (!od.is_manual) {
              if (checkoutLink) {
                window.location.href = checkoutLink;
              } else {
                toastError('No se pudo generar el link de pago');
              }
            } else {
              useSendWhatsapp(
                agent?.phone_number || event.phone || '',
                generateWhatsappMessage(event, od, orderItemData, totalToPay ?? 0),
              );
              navigate(orderSuccessPath(od.custom_id), { replace: true });
            }
          }
        } else {
          throw new Error('no order data');
        }
      })
      .catch(async (res) => {
        console.error(res);
        setClickedPay(false);
        setPayWayTokenData(null);
        setNavePaymentData(null);
        setNavePaymentRequestId(null);
        const errorData = getErrorResponse(res);
        const errorCode = errorData?.code as ErrorCode | undefined;
        if (
          errorCode === ErrorCode.InsufficientCodeBalance
          || errorCode === ErrorCode.PrivateEventInsufficientBalance
        ) {
          setShowNoBalanceModal(true);
        } else if (res?.data?.error === 'no tickets available to fulfill order') {
          toastError('Lo sentimos, las entradas que habías solicitado se agotaron.');
        } else if (errorCode === ErrorCode.PaywayRejectedBySecurity) {
          const msg = 'El pago fue rechazado por motivos de seguridad. Verificá que los datos ingresados sean correctos o probá con otra tarjeta.';
          toastError(msg);
          setPayWayFormError(msg);
        } else if (
          errorCode === ErrorCode.PaywayPaymentRejected
          || errorCode === ErrorCode.PaywayInstallmentsNotAvailable
        ) {
          // Card decline: show the reason the backend got from the issuer.
          const msg = errorData?.error || (errorData ? parseErrors(errorData) : '');
          toastError(msg);
          setPayWayFormError(msg);
        } else {
          const parsedError = errorData ? parseErrors(errorData) : '';
          toastError(parsedError || 'Se produjo un error al crear la orden. Intenta nuevamente');
        }
      });
  };

  const onSubmit = () => {
    // For PayWay, we need to wait for the token from the form
    if (paymentProvider === PaymentProvider.PAYWAY && paymentMethod === PaymentType.ONLINE && totalToPay && totalToPay > 0 && !isBackoffice) {
      // The PayWay form will handle submitting - don't process here
      // This triggers form validation, but the actual payment is handled by PayWayCheckout
      if (!payWayTokenData) {
        toastError('Por favor completá los datos de la tarjeta');
        return;
      }
      processOrder(payWayTokenData);
      return;
    }
    // For Nave, we need to create the order first to get the payment request ID
    // Then the Nave checkout will be shown
    if (paymentProvider === PaymentProvider.NAVE && paymentMethod === PaymentType.ONLINE && totalToPay && totalToPay > 0 && !isBackoffice) {
      // If we already have a Nave payment request ID, the checkout is showing
      // Don't process again - the checkout handles payment
      if (navePaymentRequestId) {
        return;
      }
      // Create order to get payment request ID - this will show the Nave checkout
      processOrder();
      return;
    }
    processOrder();
  };

  // The PayWay card form submits on its own, skipping this form's validation,
  // so the email must be checked before tokenizing the card
  const validatePayWayContact = async (): Promise<boolean> => {
    const valid = await trigger(['email', 'emailConfirm']);
    if (!valid) {
      toastError('Completá tu correo electrónico antes de pagar');
      const emailInput = document.getElementById('email');
      emailInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      emailInput?.focus({ preventScroll: true });
      return false;
    }
    return true;
  };

  const handlePayWayTokenCreated = (tokenData: PayWayTokenData) => {
    console.log('PayWay Token Created:', tokenData);
    setPayWayFormError(null);
    setPayWayTokenData(tokenData);
    // Automatically process the order with the PayWay token
    processOrder(tokenData);
  };

  const handlePayWayError = (error: string) => {
    toastError(error);
    setPayWayFormError(error);
    setClickedPay(false);
  };

  const handleNavePaymentComplete = (result: NaveCheckoutResult) => {
    console.log('Nave Payment Complete:', result);
    if (result.success && result.status === 'approved') {
      const navePayment: NavePaymentData = {
        payment_request_id: navePaymentRequestId || '',
        status: result.status,
        payment_id: result.paymentId,
      };
      setNavePaymentData(navePayment);
      // Process the order with Nave payment data
      processOrder(null, navePayment);
    } else if (result.status === 'pending') {
      toastError(result.message || 'El pago está pendiente de confirmación');
      setClickedPay(false);
    } else {
      toastError(result.message || 'El pago no pudo ser procesado');
      setClickedPay(false);
      setNavePaymentRequestId(null);
    }
  };

  const handleNaveError = (error: string) => {
    toastError(error);
    setClickedPay(false);
    setNavePaymentRequestId(null);
  };

  const handleProviderSelect = (
    provider: PaymentProvider | null,
    method: PaymentType | null,
  ) => {
    if (method === PaymentType.MANUAL) {
      setPaymentMethod(PaymentType.MANUAL);
      setPaymentProvider(null);
    } else {
      setPaymentProvider(provider);
      if (paymentMethod !== PaymentType.ONLINE) {
        setPaymentMethod(PaymentType.ONLINE);
      }
    }
  };

  const handleShare = () => {
    if (!shareData) {
      return;
    }

    if (navigator.share && typeof navigator.share === 'function') {
      navigator.share({
        title: `Entradas para ${event?.name}`,
        text: shareData.text,
        url: shareData.url,
      }).then(() => {
        setShowShareModal(false);
      }).catch((error) => {
        console.log('Error sharing:', error);
        copyLinkToClipboard(shareData.message);
      });
    } else {
      copyLinkToClipboard(shareData.message);
    }
  };

  const handleCopyToClipboard = () => {
    if (!shareData) {
      return;
    }
    copyLinkToClipboard(shareData.message);
    setShowShareModal(false);
  };

  const handlePrintTickets = () => {
    printTicketHtmlInNewWindow(() => printRef.current, {
      documentTitle: `Entradas - ${event?.name || ''}`,
      onPrepareStart: () => setIsPrintLoading(true),
      onComplete: () => setIsPrintLoading(false),
    });
  };

  if (event?.is_approved === false) {
    return (
      <div className='my-6 flex flex-col gap-6'>
        <AdminSectionTitle
          title="Eventos"
          subtitle={`${event?.name} - Venta rápida`}
        />
        <IconInfoCard
          title="Evento no aprobado"
          info="El evento no ha sido aprobado aún"
          icon={FaLink}
          iconColor="text-primary-500"
        />
      </div>
    );
  }

  if (event?.seller_sales_enabled === false && role === 'SELLER') {
    return (
      <div className='my-6 flex flex-col gap-6'>
        <AdminSectionTitle
          title="Eventos"
          subtitle={`${event?.name} - Venta rápida`}
        />
        <IconInfoCard
          title="No se puede generar entradas"
          info="Las ventas de entradas por vendedores no están habilitadas para este evento"
          icon={FaLink}
          iconColor="text-primary-500"
        />
        <div className="min-w-md mx-auto">
          <Button variant="primary" onClick={() => navigate(-1)}>
            Atrás
          </Button>
        </div>
      </div>
    );
  }

  // Computed values for conditional rendering
  const showSubmitButton = shouldShowSubmitButton(
    paymentProvider,
    paymentMethod,
    totalToPay,
    isBackoffice,
    navePaymentRequestId,
  );

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-11 lg:gap-8 ${eventpage ? '' : 'my-6 lg:my-12 lg:px-8'}`}>
      {!eventpage && <>
        {isBackoffice && <div className="col-span-11 flex flex-col gap-2">
          <AdminSectionTitle
            title="Eventos"
            subtitle={`${event?.name} - Venta rápida`}
          />
        </div>}
        {!isBackoffice && (
          <div className={`hidden xl:flex mb-8 xl:col-span-4 flex-col gap-4 ${event.public_status !== EventPublicStatus.Ready ? 'grayscale' : ''}`}>
            <Carrousel
              images={[{ url: event.banner_image, eventId: event.id }]}
            />
            <div className='glass-surface text-center lg:text-left'>
              <h1 className='text-3xl font-medium'>{event?.name}</h1>
              <span className='text-xl text-text-primary'>{parseFriendlyDate(event?.date || '', true, true)}</span>
            </div>
          </div>
        )}
      </>}
      <div className={`mb-4  ${eventpage ? 'lg:col-span-11' : `lg:col-span-11 ${isBackoffice ? 'xl:col-span-11' : 'xl:col-span-7'}`} glass-surface flex flex-col gap-4`}>
        {event?.has_showtimes && event?.showtimes && <div className='flex items-center gap-4 mb-4'>
          {!isMobile && <p className='w-full max-w-md font-medium'>Seleccioná una función</p>}
          <Select
            label={isMobile ? 'Seleccioná una función' : ''}
            id="showtime"
            options={event?.showtimes?.filter((showtime) => showtime.active && showtime.ends_at > new Date().toISOString()).sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()).map((showtime) => ({
              label: parseFriendlyDate(showtime.starts_at, true, true),
              value: showtime.id,
            }))}
            onChange={(value) => {
              setSelectedShowtimeId(value.target.value);
              setTicketSelections([]);
              setTicketsTableResetKey((prev) => prev + 1);
            }}
          />
        </div>
        }
        {event?.has_showtimes && !selectedShowtimeId && <div>
          <IconInfoCard
            title="Primero debes elegir una función"
            info="Una vez elegida la función verás entradas disponibles de cada una"
            icon={FaLink}
            iconColor="text-primary-500"
          />
        </div>}
        {event?.has_showtimes && selectedShowtimeId && entryTickets.length === 0 && <div>
          <IconInfoCard
            title="No hay entradas disponibles para esta función"
            info="Selecciona otra función para ver las entradas disponibles"
            icon={FaLink}
            iconColor="text-primary-500"
          />
        </div>}
        {!event.has_numerated_seats && <div>
          {ticketsLoading && <div className="glass-surface"><OrdersTableSkeleton /></div>}
          {(((!event?.has_showtimes && entryTickets.length > 0)) || (event?.has_showtimes && selectedShowtimeId)) && <div>
            <h1 className="text-lg p-1 text-center glass-surface mb-4">Entradas</h1>
            {!ticketsLoading && <SelectTicketsTable
              key={`entry-${ticketsTableResetKey}`}
              tickets={entryTickets}
              onTicketsChange={(updatedTicketsSelection) => handleTicketsChange(updatedTicketsSelection, 'entry')}
              coupon={appliedCoupon ?? undefined}
              codesAvailable={event.codes_available}
              maxItemsPerPerson={!isBackoffice && event.max_items_per_person ? event.max_items_per_person : null}
              otherSelections={ticketSelections.filter((selection) => selection.ticket.is_extra)}
            />}
          </div>}
          {!ticketsLoading && entryTickets.length === 0 && (!event?.has_showtimes || !selectedShowtimeId) && <div className="mt-12">
            <IconInfoCard
              title="Aún no hay entradas creadas"
              info="Aquí aparecerán todas las entradas creadas para el evento"
              icon={FaLink}
              iconColor="text-primary-500"
            />
          </div>}
          {(!ticketsLoading && ((!event?.has_showtimes && extraTickets.length > 0) || (event?.has_showtimes && selectedShowtimeId))) && <div className="mt-5">
            <h1 className="text-lg p-1 text-center glass-surface mb-4">Extras</h1>
            <SelectTicketsTable
              key={`extra-${ticketsTableResetKey}`}
              tickets={extraTickets}
              onTicketsChange={(updatedExtrasSelection) => handleTicketsChange(updatedExtrasSelection, 'extra')}
              coupon={appliedCoupon ?? undefined}
              codesAvailable={event.codes_available}
              maxItemsPerPerson={!isBackoffice && event.max_items_per_person ? event.max_items_per_person : null}
              otherSelections={ticketSelections.filter((selection) => !selection.ticket.is_extra)}
            />
          </div>}
        </div>}

        {event.has_numerated_seats && <div>
          <NumeratedSeats
            event={event}
            businessId={event.business_id}
            selectedSeats={selectedSeats}
            onSelectedSeatsChange={setSelectedSeats}
            tickets={eventTickets ?? []}
            generalTicketOrder={isBackoffice ? 'public-first' : 'price'}
          />
        </div>}

        <div>
          <Accordion
            title={`Datos de las entradas${isBackoffice && !ticketNameAndIdRequired ? ' (opcional)' : ''}`}
            isExpanded={!isBackoffice || Boolean(event?.has_audience_discounts)}
          >
            <>
              {ticketSelections.length === 0
                && <IconInfoCard
                  title="No hay entradas seleccionadas"
                  info="Aquí aparecerán las entradas que selecciones para editar sus datos"
                  icon={FaTicketAlt}
                  iconColor="text-primary-500"
                />}
              <form onSubmit={handleSubmit(onSubmit)}>
                {(ticketSelections.length > 0) && isBackoffice && !ticketNameAndIdRequired && <div className="mb-4">
                  <Switch
                    label="Personalizar entradas"
                    name="customized"
                    value="customized"
                    checked={customized}
                    onChange={() => setCustomized(!customized)}
                    disabled={event?.has_audience_discounts === true}
                  />
                </div>}
                {(ticketSelections.length > 0) && ticketSelections.map((item, index) => (
                  (customized || index === 0) && <div key={index}>
                    <div>
                      {isBackoffice && (
                        <div className={`flex flex-col mb-4 ${index !== 0 ? 'mt-6' : ''}`}>
                          <SectionTitle
                            index={index}
                            customizeData={true}
                            item={item.ticket}
                            seatName={item.seat_name}
                            event={event}
                          />
                        </div>
                      )}
                      <div className="flex flex-col gap-4">
                        <Input
                          id={`tickets.${index}.full_name`}
                          label={!ticketNameAndIdRequired ? fullNameLabel : `${fullNameLabel}*`}
                          labelAbove
                          {...register(`tickets.${index}.full_name`)}
                          errMsg={formState.errors.tickets?.[index]?.full_name?.message}
                        />
                        <Input
                          id={`tickets.${index}.person_id`}
                          label={!ticketNameAndIdRequired ? personIdLabel : `${personIdLabel}*`}
                          labelAbove
                          inputMode="numeric"
                          {...registerOnlyDigits(register(`tickets.${index}.person_id`))}
                          errMsg={formState.errors.tickets?.[index]?.person_id?.message}
                        />
                        {activeAttendeeFields.map((field) => {
                          const isNumberField = attendeeFieldType(field) === 'number';
                          return (
                            <Input
                              key={field.id}
                              id={`tickets.${index}.custom_fields.${field.id}`}
                              label={field.mandatory ? `${field.label}*` : field.label}
                              labelAbove
                              type={isNumberField ? 'number' : 'text'}
                              inputMode={isNumberField ? 'decimal' : undefined}
                              {...register(`tickets.${index}.custom_fields.${field.id}`)}
                            />
                          );
                        })}
                        {!isBackoffice && <div className="flex flex-col gap-4">
                          <Input
                            id="email"
                            label={isEmailRequired ? 'Correo electrónico*' : 'Correo electrónico'}
                            labelAbove
                            {...register('email')}
                            type="email"
                            errMsg={formState.errors.email?.message}
                          />
                          <Input
                            id="emailConfirm"
                            label={isEmailRequired ? 'Confirmar correo electrónico*' : 'Confirmar correo electrónico'}
                            labelAbove
                            {...register('emailConfirm')}
                            type="email"
                            errMsg={formState.errors.emailConfirm?.message}
                          />
                        </div>}
                        {item.ticket.has_audience_discounts && customized && (
                          <div>
                            <p className="mb-2">Descuentos por audiencia:</p>
                            <div className="flex flex-col md:flex-row gap-6">
                              {AudienceDiscounts?.data?.filter((discount) => discount.active).map((discount) => (
                                <div key={discount.id}>
                                  <RadioButton
                                    name={`audience_discount_id_${index}`}
                                    value={discount.id}
                                    label={`${discount.name} - (${toPercentage(discount.percentage)}%)`}
                                    checked={audienceDiscounts[index]?.id === discount.id}
                                    checkable={true}
                                    onChange={() => {
                                      setAudienceDiscounts({
                                        ...audienceDiscounts,
                                        [index]: audienceDiscounts[index]?.id === discount.id ? null : discount,
                                      });
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            {audienceDiscounts[index] && (
                              <p className="mt-2">
                                *Requisito: {audienceDiscounts?.[index]?.requirements}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </form>
            </>
          </Accordion>
        </div>
        {ticketSelections.length === 0
          && <CardFrame>
            <h1>Resumen</h1>
            <IconInfoCard
              title="No hay entradas seleccionadas"
              info="Aquí aparecerá el resumen de las diferentes entradas que elijas"
              icon={FaTicketAlt}
              iconColor="text-primary-500"
            />
          </CardFrame>
        }
        {ticketSelections.length > 0
          && <OrderSummary
            order={orderDataForAmounts}
            event={event}
            onCalculatedTotalToPay={(total) => { setTotalToPay(total); }}
            onLoadingTotals={() => { }}
            hasOpenPrice={false}
            items={orderItemData}
            customized={customized}
          />}
        {isBackoffice && <CardFrame>
          <h1>Enviar entradas</h1>
          {isBackoffice && <div className="flex flex-col gap-4 mt-4">
            <RadioButton
              name="share_link"
              value="share_link"
              checked={shareMethod === 'share_link'}
              label="Compartir entradas"
              onChange={() => setShareMethod('share_link')}
              descriptionUnchecked="Realizarás el envío de entradas a través de un link"
              labelClassName="text-lg font-medium"
            />
            <RadioButton
              name="send_email"
              value="send_email"
              checked={shareMethod === 'send_email'}
              label="Correo electrónico"
              onChange={() => setShareMethod('send_email')}
              descriptionUnchecked="Se enviarán por correo las entradas"
              labelClassName="text-lg font-medium"
            />
          </div>}
          {shareMethod === 'send_email' && (
            <div className="flex flex-col gap-4 mt-4">
              <Input
                id="email"
                label="Correo electrónico"
                labelAbove
                {...register('email')}
                type="email"
                errMsg={formState.errors.email?.message}
              />
              <Input
                id="email"
                label="Confirmar correo electrónico"
                labelAbove
                {...register('emailConfirm')}
                type="email"
                errMsg={formState.errors.emailConfirm?.message}
              />
            </div>
          )}
        </CardFrame>}
        <GotCoupon
          onCouponChange={handleCouponChange}
          totalToPay={totalToPay}
          isBackoffice={isBackoffice}
        />
        {/* Payment Options - With Manual Option */}
        {showPaymentOptions && (
          <CheckoutPaymentOptions
            eventId={event?.id ?? ''}
            businessId={event?.business_id ?? ''}
                paywayPromotionsOnly={event?.payway_promotions_only ?? false}
            paymentProvider={paymentProvider}
            paymentMethod={paymentMethod}
            showManualOption
            allowMercadoPago={event.allow_mercadopago_payments}
            allowUala={event.allow_uala_payments}
            allowPayWay={event.allow_payway_payments}
            allowNave={false} // nave embedded checkout disabled
            allowNaveLink
            allowPaywayLink={event.allow_payway_link_payments}
            allowZilo={event.allow_zilo_payments}
            allowStripe={event.allow_stripe_payments}
            allowFiserv={event.allow_fiserv_payments}
            disabled={clickedPay}
            onProviderSelect={handleProviderSelect}
            totalToPay={totalToPay}
            isBackoffice={isBackoffice}
            clickedPay={clickedPay}
            isPayWayProcessing={isPayWayProcessing}
            setIsPayWayProcessing={setIsPayWayProcessing}
            handlePayWayTokenCreated={handlePayWayTokenCreated}
            handlePayWayError={handlePayWayError}
            validatePayWayContact={validatePayWayContact}
            payWayFormError={payWayFormError}
            clearPayWayFormError={() => setPayWayFormError(null)}
            orderItemData={orderItemData}
            navePaymentRequestId={navePaymentRequestId}
            isNaveProcessing={isNaveProcessing}
            setIsNaveProcessing={setIsNaveProcessing}
            handleNavePaymentComplete={handleNavePaymentComplete}
            handleNaveError={handleNaveError}
          />
        )}
        {/* Payment Options - Without Manual Option */}
        {!showPaymentOptions && !isBackoffice && !!totalToPay && totalToPay > 0 && (
          <CheckoutPaymentOptions
            eventId={event?.id ?? ''}
            businessId={event?.business_id ?? ''}
                paywayPromotionsOnly={event?.payway_promotions_only ?? false}
            paymentProvider={paymentProvider}
            paymentMethod={paymentMethod}
            showManualOption={false}
            allowMercadoPago={event.allow_mercadopago_payments}
            allowUala={event.allow_uala_payments}
            allowPayWay={event.allow_payway_payments}
            allowNave={event.allow_nave_payments}
            allowNaveLink
            allowPaywayLink={event.allow_payway_link_payments}
            allowZilo={event.allow_zilo_payments}
            allowStripe={event.allow_stripe_payments}
            allowFiserv={event.allow_fiserv_payments}
            disabled={clickedPay}
            onProviderSelect={handleProviderSelect}
            totalToPay={totalToPay}
            isBackoffice={isBackoffice}
            clickedPay={clickedPay}
            isPayWayProcessing={isPayWayProcessing}
            setIsPayWayProcessing={setIsPayWayProcessing}
            handlePayWayTokenCreated={handlePayWayTokenCreated}
            handlePayWayError={handlePayWayError}
            validatePayWayContact={validatePayWayContact}
            payWayFormError={payWayFormError}
            clearPayWayFormError={() => setPayWayFormError(null)}
            orderItemData={orderItemData}
            navePaymentRequestId={navePaymentRequestId}
            isNaveProcessing={isNaveProcessing}
            setIsNaveProcessing={setIsNaveProcessing}
            handleNavePaymentComplete={handleNavePaymentComplete}
            handleNaveError={handleNaveError}
          />
        )}
        <div className="flex flex-row gap-4">
          {!eventpage && <Button variant="secondary" onClick={() => navigate(-1)}>
            Atrás
          </Button>}
          {showSubmitButton && (
            <Button variant="primary" onClick={handleSubmit(onSubmit)} loading={createOrderLoading || clickedPay || referralPending} disabled={referralPending}>
              {isBackoffice ? 'Generar' : 'Pagar'}
            </Button>
          )}
        </div>
      </div>
      <CustomModal
        contentLabel="Entradas generadas"
        isOpen={showShareModal}
        onRequestClose={() => setShowShareModal(false)}
        hideCancelButton
      >
        <div className="flex flex-col gap-4">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">¡Entradas generadas!</h2>
            <p className="text-text-secondary mb-4">
              Las entradas se han creado exitosamente para {event?.name}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={handleShare}
              className="w-full"
            >
              {navigator.share && typeof navigator.share === 'function' ? 'Compartir entradas' : 'Copiar enlace'}
            </Button>

            {navigator.share && typeof navigator.share === 'function' && (
              <Button
                variant="secondary"
                onClick={handleCopyToClipboard}
                className="w-full"
              >
                Copiar al portapapeles
              </Button>
            )}

            {printTickets.length > 0 && (
              <Button
                variant="secondary"
                onClick={handlePrintTickets}
                className="w-full flex items-center justify-center gap-2"
                loading={isPrintLoading}
              >
                Imprimir entradas
              </Button>
            )}
          </div>

          <div className="text-center">
            <Button
              variant="tertiary"
              onClick={() => {
                setShowShareModal(false);
                setCreatedOrder(null);
                setShareData(null);
              }}
              className="text-text-tertiary underline"
            >
              Cerrar sin compartir
            </Button>
          </div>
        </div>
      </CustomModal>
      {printTickets.length > 0 && (
        <div ref={printRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <PrintTicket tickets={printTickets} />
        </div>
      )}
      <PrivateEventNoBalanceModal
        isOpen={showNoBalanceModal}
        onClose={() => setShowNoBalanceModal(false)}
        onRechargeClick={() => {
          setShowNoBalanceModal(false);
          if (event?.business_id) {
            navigate(`/admin/${event.business_id}/balance`);
          }
        }}
      />
    </div>
  );
};

export default SimplifiedSalePage;
